/**
 * API クライアント共通設定
 * AWS API Gateway への fetch ラッパー
 */

// 環境変数が未設定の場合は起動時にエラーを出す（本番URLのハードコードを防ぐ）
const API_BASE_URL = process.env.REACT_APP_API_URL;
if (!API_BASE_URL) {
  throw new Error(
    "REACT_APP_API_URL が設定されていません。.env.local を確認してください。",
  );
}

/**
 * API レスポンスの共通型
 */
export interface ApiResponse<T> {
  status: "success" | "error";
  data?: T;
  message?: string;
}

/**
 * GET リクエスト
 */
export async function apiGet<T>(
  path: string,
  params?: Record<string, string | number>,
  idToken?: string,
): Promise<T> {
  const url = new URL(`${API_BASE_URL}${path}`);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      url.searchParams.append(key, String(value));
    });
  }

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (idToken) {
    headers["Authorization"] = `Bearer ${idToken}`;
  }

  const response = await fetch(url.toString(), {
    method: "GET",
    headers,
  });

  if (!response.ok) {
    throw new Error(`API エラー: ${response.status} ${response.statusText}`);
  }

  const json: ApiResponse<T> = await response.json();

  if (json.status === "error") {
    throw new Error(json.message || "サーバーエラーが発生しました");
  }

  return json.data as T;
}

/**
 * POST リクエスト
 * idToken を渡すと Authorization ヘッダーに Bearer トークンを付与する
 */
export async function apiPost<T>(
  path: string,
  body: unknown,
  idToken?: string,
): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (idToken) {
    headers["Authorization"] = `Bearer ${idToken}`;
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error(`API エラー: ${response.status} ${response.statusText}`);
  }

  const json: ApiResponse<T> = await response.json();

  if (json.status === "error") {
    throw new Error(json.message || "サーバーエラーが発生しました");
  }

  return json.data as T;
}

/**
 * DELETE リクエスト
 */
export async function apiDelete<T>(
  path: string,
  body?: unknown,
  idToken?: string,
): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (idToken) {
    headers["Authorization"] = `Bearer ${idToken}`;
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: "DELETE",
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    throw new Error(`API エラー: ${response.status} ${response.statusText}`);
  }

  const json: ApiResponse<T> = await response.json();

  if (json.status === "error") {
    throw new Error(json.message || "サーバーエラーが発生しました");
  }

  return json.data as T;
}
